import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { MorphIdentity, SceneMeshData } from './types';
import defaultSkinTone from './defaultSkinTone';

function getMeshMaterial(mesh: THREE.Mesh): THREE.Material | undefined {
  const m = mesh.material;
  if (!m) return undefined;
  return Array.isArray(m) ? m[0] : m;
}

export function hideMeshMaterials(meshData: SceneMeshData) {
  meshData.scene.traverse((child) => {
    if (child instanceof THREE.Mesh && child.type === 'SkinnedMesh') {
      const material = getMeshMaterial(child);
      if (material?.name?.includes('Hide')) {
        material.visible = false;
      }
      if (
        material?.isMaterial &&
        child.geometry?.type === 'BufferGeometry'
      ) {
        // TODO: Subdivision modifier would go here. ThreeJS has poor support for it though
        // Tried to use https://github.com/stevinz/three-subdivide and it seemed to not work well for
        // modifying an existing mesh
      }
    }
    if ((child.name ?? '').endsWith('_RND')) {
      child.visible = false;
    }
  });
}

export function turnOnShadows(meshData: SceneMeshData) {
  meshData.scene.traverse((object: THREE.Object3D) => {
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

export function disableFrustumCulled(meshData: SceneMeshData) {
  // Disable frustumCulled for children meant to be seen.
  // https://discourse.threejs.org/t/parts-of-glb-object-disappear-in-certain-angles-and-zoom/21295/4
  meshData.scene.traverse((child) => {
    if ('frustumCulled' in child) {
      const material =
        child instanceof THREE.Mesh ? getMeshMaterial(child) : undefined;
      if (child instanceof THREE.Mesh && material?.name?.includes('Hide')) {
        child.visible = false;
      } else {
        child.frustumCulled = false;
      }
    }
  });
}

export function showSkeleton(meshData: SceneMeshData) {
  const rootBone = meshData.scene.getObjectByName('root');
  if (rootBone) {
    const skeletonHelper = new THREE.SkeletonHelper(rootBone);
    (skeletonHelper.material as THREE.LineBasicMaterial).color =
      new THREE.Color(0x000000);
    meshData.scene.add(skeletonHelper);
  } else {
    console.warn('Could not find the root bone to show skeleton of');
  }
}

export function applySkinHex(skinHex?: string, charUploadID?: string) {
  const hexString = defaultSkinTone(skinHex, charUploadID);
  const hexNumber = hexStringToNumber(hexString);

  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        const material = getMeshMaterial(child);
        if (material && ['Body', 'Face'].includes(material.name)) {
          (material as THREE.MeshStandardMaterial).color.setHex(hexNumber);
          (material as THREE.MeshStandardMaterial).metalness = 0;
        }
      }
    });
  };
}

export function applyFaceExpression(faceExpression: string) {
  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    const texturePath = `/images/static_faces_textures/${faceExpression}.png`;
    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        const material = getMeshMaterial(child);
        if (material?.name === 'Face') {
          // Axel doesn't have face expressions
          if (child.parent?.name?.includes('Axel')) {
            return;
          }
          new THREE.TextureLoader().load(texturePath, (tex) => {
            tex.flipY = false;
            (material as THREE.MeshStandardMaterial).map = tex;
            material.needsUpdate = true;
          });
        }
      }
    });
  };
}

export function applyShowSkeleton(meshData: SceneMeshData, showSkeleton: boolean) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showSkeleton;
    }
    if (name.endsWith('_skl')) {
      child.visible = showSkeleton;
    }
  });
}

export function applyShowRenderingCharacter(
  meshData: SceneMeshData,
  showRendering: boolean,
) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_skl')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_RND')) {
      child.visible = showRendering;
    }
  });
}

function hexStringToNumber(hexString: string) {
  return parseInt(hexString.replace('#', ''), 16);
}

// Detect mhr rig via the glb asset.extras.rig_type tag.
// See mhr-asset-extract/README.md "## frontend-monorepo usage".
export function detectMhr(gltfAssetExtras?: { rig_type?: string }): boolean {
  return gltfAssetExtras?.rig_type === 'MHR';
}

/**
 * After character mesh load, `userData.isMhr` is set from glTF `asset.extras.rig_type`.
 * MHR assets are already Y-up for BVH playback; skip the legacy -90° X correction used for older meshes.
 */
export function isMhrScene(scene: { userData?: unknown }): boolean {
  return Boolean(
    (scene.userData as { isMhr?: boolean } | undefined)?.isMhr,
  );
}

// Set static morph target weights from identity.shapes on every SkinnedMesh in the scene that has a matching dictionary entry.
export function applyMorphShapes(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  const shapes = identity?.shapes;
  if (!meshData || !shapes) return;
  meshData.scene.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.morphTargetInfluences || !mesh.morphTargetDictionary) return;
    for (const [name, weight] of Object.entries(shapes)) {
      const idx = mesh.morphTargetDictionary[name];
      if (idx !== undefined) mesh.morphTargetInfluences[idx] = weight;
    }
  });
}

// Apply identity.joint_scales as one-shot bone[prop][axis] writes; outer coeff key is informational and ignored at apply time.
export function applyIdentityJointScales(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  if (!meshData || !identity?.joint_scales) return;
  for (const joints of Object.values(identity.joint_scales)) {
    for (const [name, props] of Object.entries(joints)) {
      const bone = meshData.scene.getObjectByName(name);
      if (!bone) continue;
      for (const [prop, axes] of Object.entries(props)) {
        const target = (bone as unknown as Record<string, Record<string, number>>)[
          prop
        ];
        if (target) Object.assign(target, axes);
      }
    }
  }
}

export const MHR_BODY_WORLD_BONE_NAME = 'body_world';

function resolveHeightScaleFactor(identity: MorphIdentity): number | null {
  const raw = identity.height_scale_factor;
  if (raw == null) return null;

  const factor = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(factor) || factor <= 0) return null;
  if (Math.abs(factor - 1) < 1e-6) return null;

  return factor;
}

/**
 * Applies identity.height_scale_factor as a uniform scale on the MHR root bone so
 * the full rig height matches scene/comic calibration without retargeting BVH.
 */
export function applyIdentityHeightScale(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  if (!meshData) return;

  const factor = resolveHeightScaleFactor(identity);
  if (factor == null) return;

  const bodyWorld = meshData.scene.getObjectByName(MHR_BODY_WORLD_BONE_NAME);
  if (!bodyWorld) {
    console.warn(
      `[mhr] height_scale_factor=${factor} but bone "${MHR_BODY_WORLD_BONE_NAME}" was not found`,
    );
    return;
  }

  bodyWorld.scale.multiplyScalar(factor);
  bodyWorld.updateMatrix();
  bodyWorld.updateMatrixWorld(true);

  meshData.scene.traverse((child) => {
    const skinned = child as THREE.SkinnedMesh;
    if (skinned.isSkinnedMesh) {
      skinned.skeleton.update();
    }
  });

  const sceneUserData = meshData.scene.userData as Record<string, unknown>;
  sceneUserData.mhrHeightScaleFactor = factor;
}
